import React, { useEffect, useState, useRef } from 'react';
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    FormControlLabel, Table, TableBody, TableCell,
    TableContainer,
    TableHead, TableRow,
    TextField,
    IconButton,
    Menu,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import moment from "moment/moment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import type { Moment } from 'moment';

type FlightClient = {
    Id?: number | string | null;
    id?: number | string | null;
    ClientCode?: string | null;
    Name?: string | null;
    PointOfSaleId?: number | string | null;
};

type FlightLog = {
    id: string | number;
    period?: string | number | null;
    start_date?: string | null;
    end_date?: string | null;
    result?: string | null;
    client?: FlightClient | null;
};

type FlightControlLogsProps = {
    onJobLogClick: (job: FlightLog) => void;
    flightLogs: FlightLog[];
    selectedClient: FlightClient | null;
    selectedJobLog: FlightLog | null;
    handleReloadData: () => void;
    tipContext: { showSuccess: (msg: string) => void; show: (msg: string) => void };
};

type LogsFilterState = {
    startDate: Moment | null;
    endDate: Moment | null;
    showErrorsOnly: boolean;
    posId: string;
};

const cssInJs = {
    header: {
        backgroundColor: "#fff",
    },
    grayBlock: {
        background: '#fafafa',
        padding: '10px',
    },
    textInput: {
        marginRight: '20px',
        marginBottom: '15px',
        marginTop: '15px'
    },
    formControl: {
        minWidth: 80,
        marginRight: '20px',
    },
    flexContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
}

const pointOfSaleMapping = {
    1113: 'Lightspeed',
    1139: 'RunIT',
    1140: 'Heartland',
    1200: 'Shopify Pilot',
    1210: 'LightSpeed R Sku v2',
    1202: 'Heartland Retail API',
    1203: 'RunIt RealTime API',
}

function FlightControlLogs({ onJobLogClick, flightLogs, selectedClient, selectedJobLog, handleReloadData }: FlightControlLogsProps) {
    const [filter, setFilter] = useState<LogsFilterState>({
        startDate: null,
        endDate: null,
        showErrorsOnly: false,
        posId: '',
    });
    const [filteredLogs, setFilteredLogs] = useState<FlightLog[]>(flightLogs);
    const [open_more, setOpenMore] = useState(false);
    const more_element = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        let _filteredLogs = [...flightLogs];

        if (filter.showErrorsOnly) {
            _filteredLogs = _filteredLogs.filter((log) => 
                log.result || (!log.end_date && moment().diff(moment(log.start_date), 'hours') >= 1)
            );
        }

        if (filter.posId) {
            _filteredLogs = _filteredLogs.filter((log) => String(log.client?.PointOfSaleId) === String(filter.posId));
        }

        if (filter.startDate && !filter.endDate) {
            _filteredLogs = _filteredLogs.filter((log) => moment(log.start_date).isSameOrAfter(filter.startDate, 'day'));
        } else if (!filter.startDate && filter.endDate) {
            _filteredLogs = _filteredLogs.filter((log) => moment(log.start_date).isSameOrBefore(filter.endDate, 'day'));
        } else if (filter.startDate && filter.endDate) {
            _filteredLogs = _filteredLogs.filter((log) => moment(log.start_date).isBetween(filter.startDate, filter.endDate, 'day'));
        }

        setFilteredLogs(_filteredLogs);
    }, [flightLogs, filter]);

    const handleTableClick = (job: FlightLog) => {
        onJobLogClick(job);
    };

    const handleCheckboxChange = () => {
        setFilter((prevFilter) => ({
            ...prevFilter,
            showErrorsOnly: !prevFilter.showErrorsOnly,
        }));
    };

    // Derive unique POS options from the logs
    const posOptions = Object.entries(pointOfSaleMapping).filter(([id]) =>
        flightLogs.some((log) => String(log.client?.PointOfSaleId) === String(id))
    );

    return (
        <Card>
            <div style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <CardHeader
                        title={'Flight Control Logs'}
                        sx={{
                            ...cssInJs.header,
                            '& .MuiTypography-root': {
                                fontSize: '16px',
                            },
                            flexGrow: 1,
                        }}
                    />
                    <IconButton onClick={() => { setOpenMore(true); }} ref={more_element}>
                        <MoreVertOutlinedIcon sx={{ color: 'black' }} />
                    </IconButton>
                    <Menu
                        open={open_more}
                        anchorEl={more_element.current}
                        onClose={() => { setOpenMore(false); }}
                    >
                        <MenuItem dense onClick={handleReloadData}>Reload Logs</MenuItem>
                    </Menu>
                </div>
                <Box sx={cssInJs.grayBlock}>
                    <div style={cssInJs.flexContainer}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DesktopDatePicker
                                label="Start date"
                                value={filter.startDate}
                                inputFormat="MMM dd"
                                mask=""
                                onChange={(e) => setFilter((prevFilter) => ({ ...prevFilter, startDate: e ? moment(e) : null }))}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth={true}
                                        variant="outlined"
                                        size="small"
                                        sx={cssInJs.textInput}
                                    />
                                )}
                            />
                        </LocalizationProvider>

                        <LocalizationProvider dateAdapter={AdapterDateFns} sx={{ m: 1 }}>
                            <DesktopDatePicker
                                label="End date"
                                value={filter.endDate}
                                inputFormat="MMM dd"
                                mask=""
                                onChange={(e) => setFilter((prevFilter) => ({ ...prevFilter, endDate: e ? moment(e) : null }))}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth={true}
                                        variant="outlined"
                                        size="small"
                                        sx={cssInJs.textInput}
                                    />
                                )}
                            />
                        </LocalizationProvider>

                        {/* POS Filter */}
                        <FormControl size="small" sx={{ minWidth: 110, mr: 1 }} disabled={!!selectedClient}>
                            <InputLabel>POS</InputLabel>
                            <Select
                                value={filter.posId}
                                label="POS"
                                onChange={(e) => setFilter((prev) => ({ ...prev, posId: String(e.target.value) }))}
                            >
                                <MenuItem value="">All</MenuItem>
                                {posOptions.map(([id, name]) => (
                                    <MenuItem key={id} value={id}>{name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Checkbox Component */}
                        <FormControlLabel
                            control={<Checkbox checked={filter.showErrorsOnly}
                                onChange={handleCheckboxChange}
                                color="primary" />}
                            label="Error"
                        />
                    </div>
                </Box>
            </div>
            <CardContent>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Client</TableCell>
                                <TableCell>Period</TableCell>
                                <TableCell>Start</TableCell>
                                <TableCell>End</TableCell>
                                <TableCell>Result</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredLogs?.length > 0 ? (
                                filteredLogs.map((job) => (
                                    <TableRow
                                        key={job.id}
                                        onClick={() => handleTableClick(job)}
                                        sx={{ cursor: 'pointer', backgroundColor: job.id == selectedJobLog?.id ? '#f0f0f0' : 'inherit' }}
                                    >
                                        <TableCell>{job.client?.Name}{job.client?.ClientCode ? ` (${job.client.ClientCode})` : ''}</TableCell>
                                        <TableCell>{job.period}</TableCell>
                                        <TableCell>{job.start_date}</TableCell>
                                        <TableCell>{job.end_date}</TableCell>
                                        <TableCell style={{ color: job.result ? 'red' : 'black', fontWeight: job.result ? 'bold' : 'normal' }}>{job.result ? 'Failed' : (job.end_date ? 'Passed' : 'Running')}</TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell align="left" colSpan={5}>
                                        No available data
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    );
}

export default FlightControlLogs
